import * as React from 'react';
import moment from 'moment-timezone';
import styled from 'styled-components';
import { withTranslation } from 'react-i18next';
import { compose } from 'react-recompose';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { Link as LinkMaterial } from '@material-ui/core';
import { Channel } from '@dev/ee-api/ee-api-models';
import {
    CaretDownMaterialIcon,
    DateDropdown,
    DetailedTable,
    DetailedTableHeadersProps,
    DetailedTableRowsProps,
    MagnifyingGlassIcon,
    ModernPagination,
    NavigateBack,
    palette,
    SecondaryButton,
    SimplePopover,
    Typography,
    WorldMap,
} from '@dev/ee-components';
import { ArrowBackIos as ArrowBackIosIcon } from '@material-ui/icons';
import { chunk, forEach, map } from 'lodash-es';

import { ApplicationState as State } from '@/store/index';
import {
    StartAllCampaignsLoading,
    StartAllChannelsLoading,
    StartLocationCountryChannelLoading,
    StartLocationCountryLoading,
    StartLocationsWorldLoading,
    StartLocationWorldChannelLoading,
} from '@/store/activity/locations/actions';
import { ShowDialog } from '@/store/dialog/actions';
import { ShowDialogParams } from '@/store/dialog/types';
import { ExportsPaths } from '@/services/dictionaries/Paths';

import { DispatchProps, GeographyProps, Props } from './GeographyProps';

const mapStateToProps = (state: State, ownProps: GeographyProps): GeographyProps => ({
    ...ownProps,
    countries: state.activity.locations.countries,
    loading: state.activity.locations.loading,
    dataSet: state.activity.locations.dataSet,
    campaigns: state.activity.locations.campaigns,
    channels: state.activity.locations.channels,
    activePage: state.activity.locations.activePage,
    countryiso: state.activity.locations.countryiso,
});

const dispatchProps = {
    StartLocationsWorldLoading,
    StartLocationWorldChannelLoading,
    StartLocationCountryLoading,
    StartLocationCountryChannelLoading,
    StartAllCampaignsLoading,
    StartAllChannelsLoading,
    ShowDialog,
};

const Nodatacontainer = styled.header`
    min-height: 50rem;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;

    @media only screen and (max-width: 1400px) {
        min-height: 30rem;
    }
`;

const mapContainer = {
    overflow: 'hidden',
    cursor: 'grab',
    margin: '4rem auto 6rem',
};

const tableContainer = {
    marginBottom: '2.4rem',
    background: '#fff',
    paddingTop: '2rem',
};

const linkStyles = {
    display: 'flex',
    alignItems: 'center',
    paddingBottom: '16px',
    width: '20rem',
};

const backtoWorldMapBtn = {
    borderRadius: '50%',
    background: palette.indigo[6],
    display: 'inline-block',
    padding: '5px',
    boxShadow: '0px 5px 12px #0D0D3630',
    marginRight: '12px',
};

const backToWorldMapContainer = {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '1rem',
    cursor: 'pointer',
};

class Geography extends React.Component<Props> {
    public state = {
        sortDirection: 'desc',
        sortType: 'Opens',
        countryClicked: false,
        selectedChannel: null,
        from: null,
        to: null,
        campaignValue: null,
        channelValue: null,
        dateRangeValue: this.props.t('daterange'),
        sortDescending: true,
        activePage: this.props.activePage,
    };

    public componentDidMount(): void {
        this.loadCountriesData();

        this.props.StartAllCampaignsLoading();
        this.props.StartAllChannelsLoading();
    }

    private loadCountriesData(): void {
        this.props.StartLocationsWorldLoading({
            from: this.state.from,
            to: this.state.to,
            sortDescending: this.state.sortDescending,
        });
    }

    private handleCountryChannelChange(): void {
        this.props.StartLocationWorldChannelLoading({
            name: this.state.selectedChannel,
            from: this.state.from,
            to: this.state.to,
        });
    }

    private handleStatesChannelChange(countryiso?: string): void {
        this.props.StartLocationCountryChannelLoading({
            countryiso: countryiso || this.props.countryiso,
            name: this.state.selectedChannel,
            from: this.state.from,
            to: this.state.to,
        });
    }

    private loadStatesCountryData(countryiso?: string): void {
        this.props.StartLocationCountryLoading({
            countryiso: countryiso || this.props.countryiso,
            from: this.state.from,
            to: this.state.to,
        });
    }

    private countAllOpens(object): number {
        let allOpens = 0;
        forEach(object, (row): void => {
            allOpens += row.OpenCount;
        });
        return allOpens;
    }

    private createTableHeader = (): DetailedTableHeadersProps[] => {
        const { t } = this.props;
        return [
            {
                id: 'name',
                numeric: false,
                label: t('country'),
                disableSort: true,
            },
            {
                id: 'opens',
                numeric: true,
                label: t('opens'),
                order: 'desc',
                disableSort: true,
            },
            {
                id: 'percentage',
                numeric: true,
                label: '',
            },
        ];
    };

    private populateRows(object): DetailedTableRowsProps[] {
        const allOpens = this.countAllOpens(object);
        return map(
            object,
            (row): DetailedTableRowsProps => ({
                id: row.CountryISO,
                name: row.Country ? row.Country : row.State,
                opens: row.OpenCount,
                rate: `${((row.OpenCount / allOpens) * 100).toFixed(2)}%`,
            })
        );
    }

    private worldMapsDataSet(): { countryId: string; name: string; value: number }[] {
        return map(
            this.props.dataSet,
            (row: { CountryISO: string; Country: string; OpenCount: number; State: string }) => ({
                countryId: row.CountryISO ? row.CountryISO.toLowerCase() : row.State,
                name: row.Country ? row.Country : row.State,
                value: row.OpenCount,
            })
        );
    }

    private paginationDataSetup(count: number, object): DetailedTableRowsProps[][] {
        return chunk(this.populateRows(object), count);
    }

    private createChannelsArray = (object): Channel[] => {
        const array = [];
        map(object, (channel): void => {
            if (channel.Name?.length > 30) {
                channel.Label = channel.Name;
            }
            array.push({ value: channel.Name, label: channel.Label || channel.Name });
        });
        array.unshift({ value: 'all', label: 'All', borderBottom: true });
        return array;
    };

    private handleRegionClick(countryId): void {
        this.state.selectedChannel ? this.handleStatesChannelChange(countryId) : this.loadStatesCountryData(countryId);
        this.setState({ countryClicked: true, activePage: 1 });
    }

    private handleReturnToWorldMap(): void {
        this.setState({ countryClicked: false, activePage: 1 }, (): void => {
            this.state.selectedChannel ? this.handleCountryChannelChange() : this.loadCountriesData();
        });
    }

    private chooseRequest(): void {
        if (this.state.selectedChannel) {
            if (this.state.countryClicked) {
                this.handleStatesChannelChange();
            } else {
                this.handleCountryChannelChange();
            }
        } else {
            if (this.state.countryClicked) {
                this.loadStatesCountryData();
            } else {
                this.loadCountriesData();
            }
        }
    }

    private renderDateRangeSelect = (): JSX.Element => {
        const { t } = this.props;
        const dateRangeData = [
            {
                label: t('last7days'),
                value: '7',
                borderBottom: true,
            },
            {
                label: t('last14days'),
                value: '14',
            },
            {
                label: t('last21days'),
                value: '21',
            },
            {
                label: t('specificdaterange'),
                value: 'calendar',
            },
        ];

        return (
            <DateDropdown
                onItemClick={(e, obj): void => {
                    const dateValue = obj.value;
                    const to = new Date();
                    let from = null;

                    if (obj.value !== 'calendar') {
                        from = moment().subtract(dateValue, 'd').utc().set({ hour: 0, minute: 0, second: 0 }).format();

                        this.setState({ from, to, dateRangeValue: obj.label }, (): void => {
                            this.chooseRequest();
                        });
                    }
                }}
                type="custom"
                options={dateRangeData}
                calendarOnChange={(start, end): void => {
                    const from = start.utc().set({ hour: 0, minute: 0, second: 0 }).format();
                    const to = end.utc().set({ hour: 0, minute: 0, second: 0 }).format();
                    const dateFromPlaceholder = start.utc().format('DD MMM YYYY');
                    const dateToPlaceholder = end.utc().format('DD MMM YYYY');

                    const dateRangeValue = `${dateFromPlaceholder} - ${dateToPlaceholder}`;

                    this.setState({ from, to, dateRangeValue }, (): void => {
                        this.chooseRequest();
                    });
                }}
            >
                <SecondaryButton
                    text={this.state.dateRangeValue}
                    color={palette.indigo[6] as any}
                    endIcon={<CaretDownMaterialIcon color={palette.indigo[6]} />}
                />
            </DateDropdown>
        );
    };

    private renderCampaignsSelect = (campaigns): JSX.Element => {
        const { t } = this.props;

        return (
            <SimplePopover
                listItemWidth={'300px'}
                type={'custom'}
                onItemClick={(e, obj): void => {
                    const selectedChannel = obj.value;

                    this.setState(
                        {
                            selectedChannel: selectedChannel === 'all' ? null : selectedChannel,
                            channelValue: null,
                            campaignValue: selectedChannel === 'all' ? null : selectedChannel,
                        },
                        (): void => {
                            this.chooseRequest();
                        }
                    );
                }}
                options={campaigns}
                listItemMaxLines={2}
                maxHeight={400}
                placement="bottom"
            >
                <SecondaryButton
                    text={t('campaigns')}
                    color={palette.indigo[6] as any}
                    endIcon={<CaretDownMaterialIcon color={palette.indigo[6]} />}
                />
            </SimplePopover>
        );
    };

    private renderChannelsSelect = (channels): JSX.Element => {
        const { t } = this.props;

        return (
            <SimplePopover
                listItemWidth={'300px'}
                type={'custom'}
                onItemClick={(e, obj): void => {
                    const selectedChannel = obj.value;

                    this.setState(
                        {
                            selectedChannel: selectedChannel === 'all' ? null : selectedChannel,
                            channelValue: selectedChannel === 'all' ? null : selectedChannel,
                            campaignValue: null,
                        },
                        (): void => {
                            this.chooseRequest();
                        }
                    );
                }}
                options={channels}
                listItemMaxLines={2}
                maxHeight={400}
                placement="bottom"
            >
                <SecondaryButton
                    text={t('channels')}
                    color={palette.indigo[6] as any}
                    endIcon={<CaretDownMaterialIcon color={palette.indigo[6]} />}
                />
            </SimplePopover>
        );
    };

    private showDeviceDetailsModal = (): void => {
        const { t, ShowDialog } = this.props;
        const params: ShowDialogParams = {
            title: t('devicedetails'),
            cancelLabel: t('close'),
            body: t('movedevicedetails'),
        };
        ShowDialog(params);
    };

    private renderLinksDropdown = (): JSX.Element => {
        const { t } = this.props;

        return (
            <div style={{ marginTop: '0' }} className="dropdown">
                <button
                    type="button"
                    className="btn btn_md btn_primary btn_caret"
                    style={{ height: '4.4rem' }}
                    data-toggle="dropdown"
                    aria-haspopup="true"
                    aria-expanded="false"
                >
                    <span id="emorereports">{t('view')}</span>
                </button>
                <ul className="dropdown-menu drop-sm">
                    <li>
                        <Link to="/activity/progress">{t('startinprogress')}</Link>
                    </li>
                    <li>
                        <Link to="/activity/emails">{t('emaillogs')}</Link>
                    </li>
                    <li>
                        <Link to="/activity/bouncedetails">{t('bouncedetails')}</Link>
                    </li>
                    <li>
                        <LinkMaterial onClick={this.showDeviceDetailsModal}>{t('devicedetails')}</LinkMaterial>
                    </li>
                    <li>
                        <Link to={ExportsPaths.Exports}>{t('exporthistory')}</Link>
                    </li>
                    <li>
                        <Link to="/activity/notifications">{t('notificationlog')}</Link>
                    </li>
                </ul>
            </div>
        );
    };

    private renderBacktoWorldMapButton = (): JSX.Element => {
        const { t } = this.props;
        return (
            <div
                id="returntoworldmap"
                style={backToWorldMapContainer}
                onClick={(): void => {
                    this.setState({ countryClicked: false }, (): void => {
                        this.state.selectedChannel ? this.handleCountryChannelChange() : this.loadCountriesData();
                    });
                }}
            >
                <div style={backtoWorldMapBtn}>
                    <NavigateBack color="#fff" />
                </div>
                <span style={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="h4">{t('backtoworldmap')}</Typography>
                </span>
            </div>
        );
    };

    private renderWorldMap = (): JSX.Element => {
        const dataSet = this.worldMapsDataSet();

        return (
            <WorldMap
                width={1168}
                height={500}
                dataSet={dataSet}
                onRegionClick={this.handleRegionClick.bind(this)}
                onReturnToWorldMapClick={this.handleReturnToWorldMap.bind(this)}
            />
        );
    };

    private paginationOnChange = (ev, newPage: number): void => {
        this.setState({ activePage: newPage });
    };

    public render(): JSX.Element {
        const { t } = this.props;
        const { activePage } = this.state;
        let campaigns = [];
        let channels = [];
        const allOpens = this.countAllOpens(this.props.dataSet);
        const headers = this.createTableHeader();

        if (this.props.channels) {
            channels = this.createChannelsArray(this.props.channels);
        }
        if (this.props.campaigns) {
            campaigns = this.createChannelsArray(this.props.campaigns);
        }

        return (
            <>
                <Link style={linkStyles} to="/activity">
                    <ArrowBackIosIcon style={{ width: '9px', height: '12px' }} />
                    {t('activity')}
                </Link>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2rem' }}>
                    <Typography variant="h1">{t('geography')}</Typography>
                    <div style={{ display: 'flex' }}>
                        {this.renderDateRangeSelect()}
                        {campaigns.length ? (
                            <div style={{ marginLeft: '5px', marginRight: '5px' }}>
                                {this.renderCampaignsSelect(campaigns)}
                            </div>
                        ) : null}
                        {channels.length ? (
                            <div style={{ marginRight: '5px' }}>{this.renderChannelsSelect(channels)}</div>
                        ) : null}
                        {this.renderLinksDropdown()}
                    </div>
                </div>
                <div style={{ minHeight: '3rem' }}>
                    {this.state.campaignValue || this.state.channelValue ? (
                        <Typography variant={'h2'}>{this.state.campaignValue || this.state.channelValue}</Typography>
                    ) : null}
                </div>
                {!allOpens && this.props.dataSet ? (
                    <Nodatacontainer>
                        <MagnifyingGlassIcon />
                        <p style={{ textAlign: 'center', color: palette.jacarta[5] }}>{t('nouseragentsdata')}</p>
                        {this.state.countryClicked ? this.renderBacktoWorldMapButton() : null}
                    </Nodatacontainer>
                ) : null}
                {allOpens ? <div style={mapContainer}>{this.renderWorldMap()}</div> : null}
                {allOpens ? (
                    <div style={tableContainer}>
                        <DetailedTable
                            headers={headers}
                            rows={this.paginationDataSetup(10, this.props.dataSet)[this.state.activePage - 1]}
                        />
                    </div>
                ) : null}
                {allOpens ? (
                    <ModernPagination
                        count={Math.ceil(this.populateRows(this.props.dataSet).length / 10)}
                        onChange={this.paginationOnChange.bind(this)}
                        page={activePage}
                    />
                ) : null}
            </>
        );
    }
}

export default compose(
    connect<GeographyProps, DispatchProps>(mapStateToProps, dispatchProps),
    withTranslation()
)(Geography);
